import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  Card,
  CardContent,
  CardActions,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  LinearProgress
} from '@mui/material';
import {
  School as SchoolIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import MainLayout from '../../components/layout/MainLayout';
import { getUserSubmissions, Problem } from '../../services/problemService';
import { getEnrollmentsByUser } from '../../services/enrollmentService';
import { getProblemsBySession } from '../../services/problemService';
import { Session } from '../../services/sessionService';
import { useAuth } from '../../contexts/AuthContext';

interface SessionData extends Session {
  problems: Problem[];
  progress: number;
  problemsSolved: number;
  totalProblems: number;
  accuracy: number;
  status: 'active' | 'past';
}

function ProblemsListPage() {
  const { authState } = useAuth();
  const { user } = authState;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionData, setSessionData] = useState<SessionData[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // Get user enrollments
        const enrollmentsData = await getEnrollmentsByUser(user.id);
        if (!enrollmentsData || !enrollmentsData.enrollments || enrollmentsData.enrollments.length === 0) {
          setSessionData([]);
          setLoading(false);
          return;
        }
        
        // Get user submissions
        const userSubmissions = await getUserSubmissions();
        
        // Process each session
        const sessionsWithProblems = await Promise.all(
          enrollmentsData.enrollments.map(async (enrollment) => {
            const session = enrollment.session;
            
            // Only include active and past sessions (date-only comparison)
            const now = new Date();
            const startDate = new Date(session.startDate);
            const endDate = new Date(session.endDate);
            
            const nowDateOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const startDateOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
            const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
            
            if (nowDateOnly < startDateOnly) {
              return null; // Skip upcoming sessions
            }
            
            // Get problems for this session
            const problems = await getProblemsBySession(session.id);
            
            // Calculate progress
            const sessionSubmissions = userSubmissions.filter(sub => 
              problems.some(prob => prob.id === sub.problemId)
            );
            
            const correctSubmissions = sessionSubmissions.filter(sub => sub.isCorrect);
            const problemsSolved = correctSubmissions.length;
            const totalProblems = problems.length;
            const progress = totalProblems > 0 ? Math.round((problemsSolved / totalProblems) * 100) : 0;
            
            // Calculate accuracy
            const accuracy = sessionSubmissions.length > 0
              ? Math.round((correctSubmissions.length / sessionSubmissions.length) * 100)
              : 0;
            
            return {
              ...session,
              problems,
              progress,
              problemsSolved,
              totalProblems,
              accuracy,
              status: (nowDateOnly <= endDateOnly ? 'active' : 'past') as 'active' | 'past'
            };
          })
        );
        
        // Filter out null values and sort by status (active first, then past)
        const filteredSessions = sessionsWithProblems
          .filter((session): session is NonNullable<typeof session> => session !== null)
          .sort((a, b) => {
            if (a.status === 'active' && b.status !== 'active') return -1;
            if (a.status !== 'active' && b.status === 'active') return 1;
            return new Date(b.endDate).getTime() - new Date(a.endDate).getTime();
          });
        
        setSessionData(filteredSessions);
      } catch (err) {
        console.error('Error fetching problem sessions:', err);
        setError('Failed to load problem sessions. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [user]);

  return (
    <MainLayout>
      <Container maxWidth="lg">
        <Box sx={{ pt: 3, pb: 6 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Math Problems
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            View and solve problems from your enrolled sessions.
          </Typography>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ my: 2 }}>
              {error}
            </Alert>
          ) : sessionData.length === 0 ? (
            <Paper sx={{ p: 3, my: 3, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                You're not enrolled in any sessions yet
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Enroll in a session to access math problems and start your learning journey!
              </Typography>
              <Button 
                variant="contained" 
                component={RouterLink} 
                to="/sessions"
                startIcon={<SchoolIcon />}
              >
                Browse Available Sessions
              </Button>
            </Paper>
          ) : (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              {sessionData.map((session) => (
                <Grid item xs={12} md={6} key={session.id}>
                  <Card elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 2 }}>
                    <CardContent sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="h6" component="h2" gutterBottom>
                          {session.title}
                        </Typography>
                        <Chip 
                          label={session.status === 'active' ? 'Active' : 'Completed'} 
                          color={session.status === 'active' ? 'success' : 'default'}
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {new Date(session.startDate).toLocaleDateString()} - {new Date(session.endDate).toLocaleDateString()}
                      </Typography>
                      
                      <Divider sx={{ my: 2 }} />
                      
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2">
                            Problems solved:
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {session.problemsSolved} / {session.totalProblems}
                          </Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={session.progress} 
                          sx={{ height: 8, borderRadius: 4, mb: 2 }}
                        />
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2">
                            Accuracy:
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {session.accuracy}%
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                    
                    <CardActions sx={{ p: 2, pt: 0 }}>
                      <Button
                        variant="contained"
                        color={session.status === 'past' ? 'secondary' : 'primary'}
                        fullWidth
                        component={RouterLink}
                        to={session.status === 'past' ? `/sessions/${session.id}/stats` : `/sessions/${session.id}/problems`}
                        endIcon={<ArrowForwardIcon />}
                      >
                        {session.status === 'past'
                          ? 'View Results'
                          : (session.progress === 0 ? 'Start Solving' : 'Continue')
                        }
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Container>
    </MainLayout>
  );
}

export default ProblemsListPage;